"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { PrizeWheelSegment } from "./PrizeWheelSegment";
import type { PrizeWheelSegmentData } from "./types";

export interface PrizeWheelProps {
  segments: PrizeWheelSegmentData[];
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  onSegmentClick?: (segment: PrizeWheelSegmentData) => void;
  onSpinComplete?: (winningSegment: PrizeWheelSegmentData) => void;
  onSpinningChange?: (spinning: boolean) => void;
  disabled?: boolean;
  /** Optional viewport scaling factors (defaults: vw=0.85, vh=0.70) */
  vwScale?: number;
  vhScale?: number;
}

export interface PrizeWheelRef {
  spin: (targetSegmentId?: string) => void;
  isSpinning: boolean;
}

const PrizeWheel = forwardRef<PrizeWheelRef, PrizeWheelProps>(
  (
    {
      segments,
      size = "md",
      className,
      onSegmentClick,
      onSpinComplete,
      onSpinningChange,
      disabled = false,
      vwScale = 0.85,
      vhScale = 0.7,
      ...props
    },
    ref,
  ) => {
    // State for managing wheel rotation and spinning
    // Initialize so pointer (3 o'clock) starts exactly on a segment boundary
    const [currentRotation, setCurrentRotation] = useState(90);
    const [isSpinning, setIsSpinning] = useState(false);
    const [animationMs, setAnimationMs] = useState(3000);

    // Ref to track spin timeout for cleanup
    const spinTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (spinTimeoutRef.current) {
          clearTimeout(spinTimeoutRef.current);
        }
      };
    }, []);

    // Calculate rotation angle for each segment
    const segmentAngle = useMemo(
      () => 360 / segments.length,
      [segments.length],
    );

    // Determine base wheel size based on size prop
    const baseWheelSize = useMemo(
      () =>
        size === "sm" ? 320 : size === "md" ? 420 : size === "lg" ? 540 : 720,
      [size],
    );

    // Responsively constrain the wheel to viewport (defaults: 85vw x 70vh)
    const [wheelSize, setWheelSize] = useState<number>(baseWheelSize);
    useEffect(() => {
      const compute = () => {
        if (typeof window === "undefined") {
          setWheelSize(baseWheelSize);
          return;
        }
        const limit = Math.min(
          window.innerWidth * vwScale,
          window.innerHeight * vhScale,
        );
        setWheelSize(Math.max(240, Math.min(baseWheelSize, Math.floor(limit))));
      };
      compute();
      window.addEventListener("resize", compute, { passive: true });
      return () => window.removeEventListener("resize", compute);
    }, [baseWheelSize, vwScale, vhScale]);

    const spin = useCallback(
      (targetSegmentId?: string) => {
        if (isSpinning || disabled) return;

        setIsSpinning(true);
        onSpinningChange?.(true);

        // Determine target segment
        const targetSegment: PrizeWheelSegmentData = targetSegmentId
          ? (segments.find((s) => s.id === targetSegmentId) ?? segments[0])
          : segments[Math.floor(Math.random() * segments.length)];

        // Find the index of the target segment
        const targetIndex = segments.findIndex(
          (s) => s.id === targetSegment.id,
        );

        // Calculate the target rotation
        // We want the pointer (at 3 o'clock) to point to the CENTER of the target segment
        // Target segment starts at this angle from top (12 o'clock)
        const targetSegmentStartAngle = targetIndex * segmentAngle;

        // Calculate the center of the target segment by adding half the segment angle
        const targetSegmentCenterAngle =
          targetSegmentStartAngle + segmentAngle / 2;

        // Calculate rotation needed to align segment CENTER with pointer
        // Pointer is at 0 degrees (3 o'clock), segments start at 90 degrees (12 o'clock)
        const targetRotation = -(targetSegmentCenterAngle - 90);

        // Choose whole rotations (3–5) for natural spin; compute travel in degrees
        const fullRotations = 3 + Math.floor(Math.random() * 3); // 3, 4, or 5

        // Compute minimal positive delta from current rotation to desired absolute target
        const normalize = (angle: number) => ((angle % 360) + 360) % 360;
        const desiredAbs = normalize(targetRotation);
        const currentAbs = normalize(currentRotation);
        let delta = desiredAbs - currentAbs;
        if (delta < 0) delta += 360; // move forward to the target center

        const travelDegrees = fullRotations * 360 + delta; // total degrees to travel

        // Duration scales with distance: baseline 5s for 3 rotations, more for longer paths
        const msPerDegree = 5000 / (3 * 360); // ~4.6296 ms/deg
        const durationMs = Math.max(
          5000,
          Math.round(travelDegrees * msPerDegree),
        );

        const finalRotation = currentRotation + travelDegrees;

        // Apply rotation with CSS transition
        setAnimationMs(durationMs);
        setCurrentRotation(finalRotation);

        // Clear any existing timeout before setting a new one
        if (spinTimeoutRef.current) {
          clearTimeout(spinTimeoutRef.current);
        }

        // Complete after the main animation without micro-snap for smoothness
        spinTimeoutRef.current = setTimeout(() => {
          setIsSpinning(false);
          onSpinningChange?.(false);
          onSpinComplete?.(targetSegment);
        }, durationMs);
      },
      [
        isSpinning,
        disabled,
        segments,
        segmentAngle,
        currentRotation,
        onSpinComplete,
        onSpinningChange,
      ],
    );

    // Expose methods through ref
    useImperativeHandle(
      ref,
      () => ({
        spin,
        isSpinning,
      }),
      [spin, isSpinning],
    );

    return (
      <div
        className={cn("relative", className)}
        style={{ width: wheelSize, height: wheelSize }}
        {...props}
      >
        {/* Spinning wheel container */}
        <div
          className="w-full h-full"
          style={{
            transform: `rotate(${currentRotation}deg)`,
            transition: `transform ${animationMs}ms cubic-bezier(0.23, 1, 0.32, 1)`,
            willChange: "transform",
          }}
        >
          {/* Segments arranged in a circle */}
          {segments.map((segment, index) => {
            const rotation = index * segmentAngle;

            return (
              <PrizeWheelSegment
                key={segment.id}
                data={segment}
                size={size}
                rotation={rotation}
                segmentAngle={segmentAngle}
                wheelSize={wheelSize}
                onClick={disabled || isSpinning ? undefined : onSegmentClick}
              />
            );
          })}
        </div>

        {/* Center overlay with wheel spinner - matching Figma design */}
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20"
          style={{ width: wheelSize * 0.35, height: wheelSize * 0.35 }}
        >
          {/* Wheel spinner image replacing center circle and pointer */}
          <div
            className="relative w-full h-full"
            style={{ width: "100%", height: "100%" }}
          >
            <Image
              src="/assets/prize-wheel/wheel-spinner.webp"
              alt="Wheel spinner"
              width={Math.round(wheelSize * 0.35)}
              height={Math.round(wheelSize * 0.35)}
              className="object-contain w-full h-full"
              priority
            />
          </div>
        </div>
      </div>
    );
  },
);

export { PrizeWheel };
